import { format } from "date-fns";

import type { AcknowledgementReportRowDto } from "@/generated/model";
import { formatAcknowledgementDateTime } from "@/lib/acknowledgements/tracking";
import { buildCsv } from "@/lib/csv";

/**
 * Columns, in order. AC16's record fields plus AC24's dates, which is what a
 * manager pivots on and what an inspector asks for.
 */
const COLUMNS = [
  "Staff member",
  "Email",
  "Role",
  "Document",
  "Type",
  "Version",
  "Sent",
  "Read on",
  "Signed on",
  "Last reminded",
  "Status",
] as const;

/**
 * Builds the acknowledgement report as CSV text.
 *
 * Client-side because the API has no file-download path at all — the only
 * "produce a document" precedent in this codebase is a server-rendered PDF, and
 * that is a much bigger piece of work. The endpoint hands back rows and this
 * turns them into a file.
 */
export function buildAcknowledgementReportCsv(
  rows: AcknowledgementReportRowDto[],
): string {
  return buildCsv(
    COLUMNS,
    rows.map((row) => [
      row.staffName ?? "",
      row.staffEmail,
      row.role ?? "",
      row.itemTitle,
      row.itemType,
      String(row.itemVersion),
      formatAcknowledgementDateTime(row.sentAt) ?? "",
      formatAcknowledgementDateTime(row.readAt) ?? "",
      formatAcknowledgementDateTime(row.signedAt) ?? "",
      formatAcknowledgementDateTime(row.lastRemindedAt) ?? "",
      row.state,
    ]),
  );
}

/** Stable, sortable filename. */
export function acknowledgementReportFilename(
  itemTitle: string,
  version: number,
  now: Date = new Date(),
): string {
  const slug =
    itemTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "acknowledgements";
  return `${slug}-v${version}-${format(now, "yyyy-MM-dd")}.csv`;
}
